import { useEffect } from 'react';

import { useMatchConnection } from '@lib/live/connection/use-match-connection';
import type { GetAccessToken } from '@lib/live/connection/realtime-client';
import type { ConnectionPhase, MatchState } from '@lib/live/match/types';

export type SubscribedMatchState = {
  state: MatchState;
  connectionPhase: ConnectionPhase;
  isLive: boolean;
  error: string | null;
  /** Whether match-level state has arrived; see the hook for why it matters. */
  hasMainSynced: boolean;
};

type MatchSubscriberProps = {
  matchId: string;
  /** Opta ids of the squad players featuring in this match. */
  playerOptaIds: string[];
  getAccessToken: GetAccessToken;
  onChange: (matchId: string, state: SubscribedMatchState) => void;
  onRemove: (matchId: string) => void;
};

/**
 * Headless adapter that runs a per-match connection lifecycle and
 * pushes the current state up to a parent. Renders nothing. Used by
 * the team-live provider to fan in N independent match channels into
 * a single state map.
 */
export const MatchSubscriber = ({
  matchId,
  playerOptaIds,
  getAccessToken,
  onChange,
  onRemove
}: MatchSubscriberProps) => {
  const { state, connectionPhase, isLive, error, hasMainSynced } =
    useMatchConnection({ matchId, playerOptaIds, getAccessToken });

  useEffect(() => {
    onChange(matchId, {
      state,
      connectionPhase,
      isLive,
      error,
      hasMainSynced
    });
  }, [matchId, state, connectionPhase, isLive, error, hasMainSynced, onChange]);

  // Evict this match's entry when the subscriber unmounts (team refetch
  // changes optaMatchIds, gameweek rollover). Without this the parent
  // map holds the last-known state forever and a stale entry can keep
  // reporting the team as live.
  useEffect(() => {
    return () => onRemove(matchId);
  }, [matchId, onRemove]);

  return null;
};
